/* =========================================================================
   Add / edit recipe form, including AI-assisted import.

   The import flow tries the Claude endpoint first and silently falls back to
   the local regex parser when the endpoint is missing, unconfigured, or the
   device is offline. The user is always told which engine produced the result,
   because the two have very different accuracy.
   ========================================================================= */

import { openModal, closeModal, toast, registerActions, val, $ } from './dom.js'
import {
  CATEGORIES, esc, ingLine, parseIngLine, CAT_EMOJI,
  tidyRecipeText, tidyIngredientItem, tidyMethodStep,
} from '../data/model.js'
import { parseRecipeText, parseRecipeRemote, looksLikeBareUrl } from '../data/parser.js'
import { byId, addRecipe, saveRecipe } from '../data/store.js'
import { state, render, showList } from '../app.js'

/** Notes returned by the AI parser, attached when the recipe is saved. */
let pendingNotes = []

export function openRecipeForm(r) {
  const isNew = !r
  pendingNotes = []

  openModal(`
    <h3>${isNew ? 'Add recipe' : 'Edit recipe'}</h3>
    ${isNew ? `
    <label for="fPaste">Paste a link, or the recipe text</label>
    <textarea id="fPaste" placeholder="Paste a recipe URL, or the whole thing — an Instagram caption, an article, an email. Messy is fine."></textarea>
    <div class="actions" style="justify-content:flex-start">
      <button class="btn small primary" data-act="smartParse">✨ Auto-fill from text or link</button>
      <button class="btn small" data-act="parsePhoto">📷 From a screenshot</button>
    </div>
    <div class="hint" id="parseHint">
      A link on its own works — the page is fetched for you. So does a screenshot
      of an Instagram post or a cookbook page. Always check the fields below
      before saving.
    </div>` : ''}

    <label for="fTitle">Title</label>
    <input type="text" id="fTitle" value="${esc(r ? r.title : '')}">

    <div class="row">
      <div>
        <label for="fCat">Category</label>
        <select id="fCat">${CATEGORIES.map(c => {
          const sel = r ? r.category === c : c === 'Other'
          return `<option ${sel ? 'selected' : ''}>${c}</option>`
        }).join('')}</select>
      </div>
      <div>
        <label for="fServes">Serves (original — source of truth)</label>
        <input type="number" id="fServes" min="1" inputmode="numeric" value="${r ? r.serves : 4}">
      </div>
    </div>

    <label for="fTags">Tags (comma separated)</label>
    <input type="text" id="fTags" value="${esc(r ? (r.tags || []).join(', ') : '')}" placeholder="quick, BBQ, kid-friendly">

    <label for="fSource">Source — chef / restaurant / where it came from</label>
    <input type="text" id="fSource" value="${esc(r && r.source ? r.source.name : '')}" placeholder="e.g. Adapted from Karen Martini, Good Weekend">

    <label for="fUrl">Source link (optional)</label>
    <input type="url" id="fUrl" inputmode="url" value="${esc(r && r.source ? r.source.url : '')}" placeholder="https://…">

    <label for="fIng">Ingredients — one per line (quantity first, e.g. “2 tbsp olive oil”)</label>
    <textarea id="fIng">${esc(r ? r.ingredients.map(i => ingLine(i)).join('\n') : '')}</textarea>

    <label for="fMethod">Method — one step per line</label>
    <textarea id="fMethod">${esc(r ? r.method.join('\n') : '')}</textarea>

    <div class="actions">
      <button class="btn" data-act="closeModal">Cancel</button>
      <button class="btn primary" data-act="saveRecipeForm" data-id="${r ? r.id : ''}">${isNew ? 'Add recipe' : 'Save changes'}</button>
    </div>`)
}

/* ---------------- applying a parsed result ---------------- */

function hint(html) {
  const el = $('parseHint')
  if (el) el.innerHTML = html
}

function busy(on, label) {
  const btns = document.querySelectorAll('[data-act="smartParse"],[data-act="parsePhoto"]')
  btns.forEach(b => { b.disabled = on })
  if (on) hint(`<b>${label}</b> — this usually takes a few seconds.`)
}

/** Push a structured recipe object into the form fields. */
function applyToForm(rec, via) {
  if (rec.title) $('fTitle').value = rec.title
  if (rec.serves) $('fServes').value = rec.serves
  if (rec.sourceName && !val('fSource').trim()) $('fSource').value = rec.sourceName
  if (rec.url && !val('fUrl').trim()) $('fUrl').value = rec.url

  if (rec.category) {
    const sel = $('fCat')
    const match = [...sel.options].find(o => o.value === rec.category)
    if (match) sel.value = rec.category
  }

  if (Array.isArray(rec.tags) && rec.tags.length && !val('fTags').trim()) {
    $('fTags').value = rec.tags.filter(Boolean).join(', ')
  }

  // Apply house style here, not just on save, so what you review in the form
  // is exactly what gets stored.
  const ingLines = (rec.ingredients || []).map(i =>
    typeof i === 'string'
      ? tidyIngredientItem(i)
      : ingLine({ qty: i.qty, unit: i.unit || '', item: tidyIngredientItem(i.item || '') }))
  if (ingLines.length) $('fIng').value = ingLines.join('\n')

  const steps = (rec.method || []).map(tidyMethodStep).filter(Boolean)
  if (steps.length) $('fMethod').value = steps.join('\n')

  pendingNotes = (rec.notes || []).filter(Boolean).map(t => String(t))

  const ni = ingLines.length, nm = (rec.method || []).length
  const engine = via === 'json-ld'
    ? 'Read the structured recipe data from the page'
    : via === 'claude'
      ? 'Parsed by Claude'
      : 'Parsed on this device (basic pattern matching)'

  if (!ni && !nm) {
    hint(`<b>Nothing usable found.</b> Try pasting the ingredients and method directly.`)
    toast("Couldn't read that")
    return
  }

  const extras = []
  if (pendingNotes.length) extras.push(`${pendingNotes.length} note${pendingNotes.length === 1 ? '' : 's'} captured`)
  hint(`✅ <b>${engine}.</b> ${ni} ingredients, ${nm} steps${extras.length ? ' · ' + extras.join(' · ') : ''}. <b>Check the fields below before saving.</b>`)
  toast(`${ni} ingredients, ${nm} steps — please review`)
}

/** Local regex fallback, applied through the same path. */
function applyLocal(raw, reason) {
  const out = parseRecipeText(raw)
  applyToForm(out, 'local')
  if (reason) {
    const el = $('parseHint')
    if (el) el.innerHTML += `<br><span style="color:#b3541e">${reason}</span>`
  }
}

/* ---------------- actions ---------------- */

async function smartParse() {
  const raw = val('fPaste')
  if (!raw.trim()) { toast('Paste a link or some recipe text first'); return }

  const isUrl = looksLikeBareUrl(raw)
  if (isUrl) $('fUrl').value = raw.trim()

  busy(true, isUrl ? 'Fetching and reading the page' : 'Reading the recipe')
  try {
    const { via, recipe } = await parseRecipeRemote(isUrl ? { url: raw.trim() } : { text: raw })
    applyToForm(recipe, via)
  } catch (e) {
    if (isUrl) {
      // The local parser can't fetch a page — there is nothing to fall back to.
      hint(`<b>Couldn't fetch that link.</b> ${explain(e)}<br>
        Open the page yourself, select the recipe text, and paste that instead.`)
      toast("Couldn't fetch that link")
    } else {
      applyLocal(raw, `Claude wasn't available (${explain(e)}), so this used the basic on-device parser — check it carefully.`)
    }
  } finally {
    busy(false)
  }
}

function parsePhoto() {
  const inp = document.createElement('input')
  inp.type = 'file'
  inp.accept = 'image/*'
  inp.onchange = async () => {
    const f = inp.files[0]
    if (!f) return
    busy(true, 'Reading the photo')
    try {
      const { via, recipe } = await parseRecipeRemote({ image: f })
      applyToForm(recipe, via)
    } catch (e) {
      hint(`<b>Couldn't read that photo.</b> ${explain(e)}<br>
        Photo reading needs the Claude endpoint — it can't run on this device.`)
      toast("Couldn't read that photo")
    } finally {
      busy(false)
    }
  }
  inp.click()
}

function explain(e) {
  if (!navigator.onLine) return "you're offline"
  if (e?.code === 'no-endpoint') return 'the AI endpoint is not deployed here'
  if (/not configured/i.test(e?.message || '')) return 'no API key is attached to this deployment'
  return esc(e?.message || 'unknown error')
}

async function saveRecipeForm(d) {
  const title = val('fTitle').trim()
  if (!title) { toast('Please add a title'); return }

  const data = {
    title,
    category: val('fCat'),
    serves: Math.max(1, parseInt(val('fServes'), 10) || 4),
    tags: val('fTags').split(',').map(t => t.trim()).filter(Boolean),
    source: { name: val('fSource').trim(), url: val('fUrl').trim() },
    ingredients: val('fIng').split(/\r?\n/).map(parseIngLine).filter(Boolean),
    method: val('fMethod').split(/\r?\n/).map(s => s.trim()).filter(Boolean),
  }

  // House style: lowercase ingredient lines, sentence-case method steps.
  // addRecipe() applies this via normaliseRecipe(); an edit needs it here.
  tidyRecipeText(data)

  const id = d.id
  if (id) {
    const r = byId(id)
    Object.assign(r, { ...data, emoji: CAT_EMOJI[data.category] || r.emoji })
    delete state.servesOverride[id]
    await saveRecipe(r)
    closeModal()
    render()
    toast('Saved')
  } else {
    const today = new Date().toISOString().slice(0, 10)
    data.notes = pendingNotes.map(text => ({ text, date: today }))
    pendingNotes = []
    await addRecipe(data)
    closeModal()
    showList()
    toast('Recipe added')
  }
}

registerActions({
  openAdd: () => openRecipeForm(null),
  openEdit: d => openRecipeForm(byId(d.id)),
  smartParse,
  parsePhoto,
  saveRecipeForm,
})
